"use client";

import React, { Suspense, lazy, useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { LocationContent, LocationVariant, buildFullAddress, generateMapEmbedUrl } from "./types";
import {
  PiMapPin,
  PiPhone,
  PiEnvelope,
  PiClock,
  PiArrowRight,
  PiPlus,
  PiMinus,
  PiGlobe,
  PiLink,
} from "react-icons/pi";

// ============================================================
// LOCATION SECTION - Main Component
// 4 variants: Full-Width, Split, Sidebar, Multi-Location
// No backend API - pure frontend content management
// ============================================================

interface LocationSectionProps extends Omit<SectionContentProps, "content"> {
  content: LocationContent;
}

// Variant components (lazy loaded for code splitting)
const FullWidthLocation = lazy(() => import("./variants/FullWidthLocation"));
const SplitLocation = lazy(() => import("./variants/SplitLocation"));
const SidebarLocation = lazy(() => import("./variants/SidebarLocation"));
const MultiLocation = lazy(() => import("./variants/MultiLocation"));

// Variant registry for switcher
export const LOCATION_VARIANTS = [
  { id: "full-width" as LocationVariant, label: "Full Width", preview: "🗺️", description: "Hero-style map with info bar" },
  { id: "split" as LocationVariant, label: "Split Layout", preview: "📍", description: "Map and info side by side" },
  { id: "sidebar" as LocationVariant, label: "Sidebar", preview: "📋", description: "Contact info sidebar with map" },
  { id: "multi" as LocationVariant, label: "Multi Location", preview: "🏢", description: "Multiple office locations" },
] as const;

// Loading skeleton
const LocationSkeleton: React.FC = () => (
  <div className="animate-pulse bg-slate-200 h-[400px] w-full rounded-lg" role="status" aria-label="Loading location section" />
);

// Main Location Section Component
export default function LocationSection({ content: propContent, onContentChange, isSelected }: LocationSectionProps) {
  const content = propContent || {};
  const variant = content.variant || "full-width";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Variant switcher (only visible when selected and editing)
  const showVariantSwitcher = isSelected && onContentChange;

  // Get the appropriate component
  const getVariantComponent = () => {
    switch (variant) {
      case "full-width":
        return <FullWidthLocation content={content} onContentChange={onContentChange} isSelected={isSelected} />;
      case "split":
        return <SplitLocation content={content} onContentChange={onContentChange} isSelected={isSelected} />;
      case "sidebar":
        return <SidebarLocation content={content} onContentChange={onContentChange} isSelected={isSelected} />;
      case "multi":
        return <MultiLocation content={content} onContentChange={onContentChange} isSelected={isSelected} />;
      default:
        return <FullWidthLocation content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }
  };

  return (
    <div className={`relative ${isSelected ? "ring-2 ring-blue-500 rounded-lg" : ""}`} style={{ fontFamily }}>
      {/* Variant Switcher Toolbar */}
      {showVariantSwitcher && (
        <div className="absolute top-4 right-4 z-20 flex bg-white rounded-lg shadow-lg border border-gray-200 p-1">
          {LOCATION_VARIANTS.map((v) => (
            <button
              key={v.id}
              onClick={() => onContentChange?.({ ...content, variant: v.id })}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${variant === v.id
                ? "bg-blue-50 text-blue-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              title={v.description}
            >
              <span>{v.preview}</span>
              <span className="capitalize hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Variant Component with Suspense */}
      <Suspense fallback={<LocationSkeleton />}>
        {getVariantComponent()}
      </Suspense>
    </div>
  );
}

// ============================================================
// LOCATION SWITCHER COMPONENT (for template selection UI)
// ============================================================

interface LocationSwitcherProps {
  selectedVariant: LocationVariant;
  onSelect: (variant: LocationVariant) => void;
  className?: string;
}

export function LocationSwitcher({ selectedVariant, onSelect, className = "" }: LocationSwitcherProps) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`}>
      {LOCATION_VARIANTS.map((v) => (
        <button
          key={v.id}
          onClick={() => onSelect(v.id)}
          className={`
            p-4 rounded-xl border-2 text-left transition-all
            ${selectedVariant === v.id
              ? "border-blue-500 bg-blue-50"
              : "border-slate-200 bg-white hover:border-slate-300"
            }
          `}
        >
          <span className="text-2xl mb-2 block">{v.preview}</span>
          <span className="font-semibold text-slate-900 block text-sm">{v.label}</span>
          <span className="text-xs text-slate-500">{v.description}</span>
        </button>
      ))}
    </div>
  );
}

// ============================================================
// QUICK COMPONENT FOR SIMPLE USAGE
// ============================================================

interface QuickLocationProps {
  variant?: LocationVariant;
  content?: Record<string, any>;
  className?: string;
}

export function QuickLocation({ variant = "full-width", content = {}, className = "" }: QuickLocationProps) {
  const variantMap: Record<string, React.ComponentType<any>> = {
    "full-width": FullWidthLocation,
    "split": SplitLocation,
    "sidebar": SidebarLocation,
    "multi": MultiLocation,
  };
  const Component = variantMap[variant] || FullWidthLocation;
  return <Component content={content} className={className} />;
}

// Export variants for direct imports
export { default as FullWidthLocation } from "./variants/FullWidthLocation";
export { default as SplitLocation } from "./variants/SplitLocation";
export { default as SidebarLocation } from "./variants/SidebarLocation";
export { default as MultiLocation } from "./variants/MultiLocation";
